"use client";

import { useId, useState } from "react";
import {
  ArrowUpRight,
  BadgeCheck,
  CircleAlert,
  Info,
  Sparkles,
  TrendingUp,
  type LucideIcon,
} from "lucide-react";

type ReportRange = "daily" | "weekly" | "monthly" | "yearly";

type MetricTone = "positive" | "caution" | "steady";
type InsightTone = "positive" | "caution" | "opportunity";

type ReportMetric = {
  label: string;
  value: string;
  delta: string;
  tooltip: string;
  tone: MetricTone;
};

type ReportInsight = {
  id: string;
  title: string;
  summary: string;
  detail: string;
  owner: string;
  impact: string;
  tone: InsightTone;
};

type ReportPoint = {
  label: string;
  value: number;
  display: string;
};

type ReportDataset = {
  label: string;
  sentAt: string;
  headline: string;
  message: string;
  summaryLine: string;
  actionTitle: string;
  actionBody: string;
  actionWindow: string;
  actionImpact: string;
  chartTitle: string;
  chart: ReportPoint[];
  metrics: ReportMetric[];
  insights: ReportInsight[];
};

const reportDatasets: Record<ReportRange, ReportDataset> = {
  daily: {
    label: "Daily brief",
    sentAt: "Sent 7:00 AM",
    headline: "Good morning. Revenue held up, but two products are leaking easy wins.",
    message:
      "Yesterday finished strong on bundles and repeat buyers, but two mobile product flows still need attention.",
    summaryLine: "Up day overall. The main miss is mobile conversion on two hero products.",
    actionTitle: "Inspect mobile checkout on Linen Travel Bag and Ceramic Brew Set.",
    actionBody:
      "Check shipping clarity, payment friction, and price framing before noon.",
    actionWindow: "Best done before 12:00",
    actionImpact: "Potential lift: 6 to 9 extra orders today",
    chartTitle: "Last 7 days revenue trend",
    chart: [
      { label: "Mon", value: 12.4, display: "$12.4k" },
      { label: "Tue", value: 13.8, display: "$13.8k" },
      { label: "Wed", value: 12.9, display: "$12.9k" },
      { label: "Thu", value: 15.2, display: "$15.2k" },
      { label: "Fri", value: 16.4, display: "$16.4k" },
      { label: "Sat", value: 18.1, display: "$18.1k" },
      { label: "Sun", value: 18.9, display: "$18.9k" },
    ],
    metrics: [
      {
        label: "Revenue",
        value: "$18.9k",
        delta: "+8.4% vs prior day",
        tooltip: "Gross revenue booked yesterday, before returns and refunds settle.",
        tone: "positive",
      },
      {
        label: "Conversion",
        value: "2.9%",
        delta: "0.3 pts below trend",
        tooltip: "Storewide session-to-order conversion compared with the rolling 14-day baseline.",
        tone: "caution",
      },
      {
        label: "Returning customers",
        value: "38%",
        delta: "+4.2 pts",
        tooltip: "Share of revenue from returning customers, a useful signal for retention quality.",
        tone: "steady",
      },
    ],
    insights: [
      {
        id: "checkout-friction",
        title: "Traffic rose faster than conversion",
        summary: "Two high-traffic products pulled clicks, but not enough completed orders.",
        detail:
          "Those products generated 31% of views but only 18% of orders. Shipping clarity and price framing are the first places to check.",
        owner: "Growth lead",
        impact: "Fast lift if fixed",
        tone: "caution",
      },
      {
        id: "bundle-margin",
        title: "Bundle offers carried the day",
        summary: "Bundles raised order value without needing heavier discounting.",
        detail:
          "Attach rate rose to 14%, which lifted order value and kept margin healthier than a discount-led push.",
        owner: "Merchandising",
        impact: "Keep pushing this pattern",
        tone: "positive",
      },
    ],
  },
  weekly: {
    label: "Weekly review",
    sentAt: "Prepared every Monday",
    headline: "This week stayed healthy overall, but margin slipped where competitors pushed price first.",
    message:
      "Traffic held up, bundles kept order value strong, and two fast-moving SKUs now need pricing or positioning attention.",
    summaryLine: "The week was solid, but a few exposed products need action before next week compounds the leak.",
    actionTitle: "Review pricing and mobile proof on the three SKUs that lost conversion after competitor movement.",
    actionBody:
      "Check whether the issue is price, positioning, or page friction, then decide whether to adjust price or improve product proof.",
    actionWindow: "Best done in the next 48 hours",
    actionImpact: "Expected result: protect margin while recovering conversion on the exposed SKUs",
    chartTitle: "Revenue through the week",
    chart: [
      { label: "Mon", value: 41, display: "$41k" },
      { label: "Tue", value: 43, display: "$43k" },
      { label: "Wed", value: 45, display: "$45k" },
      { label: "Thu", value: 44, display: "$44k" },
      { label: "Fri", value: 48, display: "$48k" },
      { label: "Sat", value: 51, display: "$51k" },
      { label: "Sun", value: 53, display: "$53k" },
    ],
    metrics: [
      {
        label: "Revenue",
        value: "$325k",
        delta: "+6.8% vs prior week",
        tooltip: "Gross revenue booked across the last completed week compared with the previous seven days.",
        tone: "positive",
      },
      {
        label: "Gross margin",
        value: "46.1%",
        delta: "1.1 pts below target",
        tooltip: "Gross margin across the week, reflecting discount pressure and pricing changes in the mix.",
        tone: "caution",
      },
      {
        label: "Competitor gaps",
        value: "3 SKUs",
        delta: "2 new exposures",
        tooltip: "Products where competitor pricing or positioning likely contributed to weaker conversion this week.",
        tone: "steady",
      },
    ],
    insights: [
      {
        id: "competitor-pressure",
        title: "Competitor movement is now visible in the weekly pattern",
        summary: "Three products saw conversion soften after nearby price changes in the market.",
        detail:
          "Traffic stayed healthy on those products, which points away from demand loss and more toward price framing, offer quality, or competitive positioning.",
        owner: "Commercial lead",
        impact: "Worth fixing this week",
        tone: "caution",
      },
      {
        id: "bundle-support",
        title: "Bundles kept the week stronger than the exposed SKUs suggest",
        summary: "Higher-value bundles absorbed more of the growth and protected average order value.",
        detail:
          "Without the bundle mix, the week would have looked flatter. That is useful because it shows exactly where the stronger offer still exists.",
        owner: "Merchandising",
        impact: "Scale what is working",
        tone: "positive",
      },
    ],
  },
  monthly: {
    label: "Monthly review",
    sentAt: "Prepared on the 1st",
    headline: "March looks healthier than February: cleaner spend, stronger margin, better product mix.",
    message:
      "Paid traffic became more efficient, discounts mattered less, and kitchen bundles were the clearest growth engine.",
    summaryLine: "Better month, with healthier growth and fewer forced promotions.",
    actionTitle: "Shift 15% of paid budget into high-margin kitchen bundles.",
    actionBody:
      "Move spend away from low-intent mug campaigns and back the bundle pages that are converting cleanly.",
    actionWindow: "Best done this week",
    actionImpact: "Expected result: stronger margin with similar top-line volume",
    chartTitle: "Weekly revenue during the month",
    chart: [
      { label: "W1", value: 68, display: "$68k" },
      { label: "W2", value: 72, display: "$72k" },
      { label: "W3", value: 79, display: "$79k" },
      { label: "W4", value: 84, display: "$84k" },
    ],
    metrics: [
      {
        label: "Revenue",
        value: "$303k",
        delta: "+11.7% vs last month",
        tooltip: "Gross revenue booked during the current month compared with the previous full month.",
        tone: "positive",
      },
      {
        label: "Gross margin",
        value: "47.8%",
        delta: "+2.6 pts",
        tooltip: "Gross margin improved as bundles and lower discount pressure carried more of the mix.",
        tone: "positive",
      },
      {
        label: "CAC",
        value: "$31",
        delta: "-9%",
        tooltip: "Blended customer acquisition cost across paid channels.",
        tone: "positive",
      },
    ],
    insights: [
      {
        id: "bundle-engine",
        title: "A small SKU group is driving most of the improvement",
        summary: "Top kitchen bundles generated the cleanest growth this month.",
        detail:
          "The top 20% of SKUs generated 64% of monthly growth, led by products with stronger proof and cleaner mobile pages.",
        owner: "Merchandising",
        impact: "Scale this pattern",
        tone: "positive",
      },
      {
        id: "paid-efficiency",
        title: "Paid search got cheaper and smarter",
        summary: "Spend quality improved after weak generic campaigns were trimmed.",
        detail:
          "CAC dropped while branded and mid-intent terms held volume. One mug campaign cluster still spends too freely.",
        owner: "Performance marketing",
        impact: "Easy budget reallocation",
        tone: "opportunity",
      },
    ],
  },
  yearly: {
    label: "Yearly outlook",
    sentAt: "Updated this quarter",
    headline: "The business is stronger than last year: larger baskets, better retention, fewer panic discounts.",
    message:
      "Growth depended less on one peak season, while repeat customers and order value both improved.",
    summaryLine: "The business is maturing: better retention, better baskets, less promo pressure.",
    actionTitle: "Invest next in retention flows, not broader discounting.",
    actionBody:
      "Double down on post-purchase and win-back sequences before broadening top-of-funnel spend.",
    actionWindow: "Best planned this quarter",
    actionImpact: "Likely outcome: better repeat revenue with less margin erosion",
    chartTitle: "Quarterly revenue trajectory",
    chart: [
      { label: "Q1", value: 620, display: "$620k" },
      { label: "Q2", value: 710, display: "$710k" },
      { label: "Q3", value: 760, display: "$760k" },
      { label: "Q4", value: 910, display: "$910k" },
    ],
    metrics: [
      {
        label: "Revenue",
        value: "$3.0M",
        delta: "+18% YoY",
        tooltip: "Gross revenue compared against the previous 12-month period.",
        tone: "positive",
      },
      {
        label: "Repeat revenue",
        value: "42%",
        delta: "+7 pts",
        tooltip: "Share of revenue driven by returning customers across the year.",
        tone: "positive",
      },
      {
        label: "Average order value",
        value: "$86",
        delta: "+12%",
        tooltip: "Average order value across all completed orders this year.",
        tone: "positive",
      },
    ],
    insights: [
      {
        id: "retention-engine",
        title: "Retention moved from support act to growth engine",
        summary: "Returning customers generated far more of the year's growth.",
        detail:
          "Repeat revenue rose without heavier promotions, a strong sign that product quality and lifecycle messaging are working together.",
        owner: "CRM and lifecycle",
        impact: "Best lever for next year",
        tone: "positive",
      },
      {
        id: "holiday-discipline",
        title: "Holiday performance improved without sacrificing margin",
        summary: "Peak season was bigger and less discount-led than last year.",
        detail:
          "The store sold through the strongest window with less discount pressure, which is exactly the kind of progress worth protecting.",
        owner: "Commercial lead",
        impact: "Model is maturing",
        tone: "opportunity",
      },
    ],
  },
};

const defaultRangeOrder: ReportRange[] = ["daily", "monthly", "yearly"];

const compactRangeLabels: Record<ReportRange, string> = {
  daily: "Daily",
  weekly: "Weekly",
  monthly: "Monthly",
  yearly: "Yearly",
};

const metricToneClasses: Record<MetricTone, string> = {
  positive: "border-emerald-400/20 bg-emerald-400/10 text-emerald-100",
  caution: "border-amber-400/20 bg-amber-400/10 text-amber-100",
  steady: "border-sky-400/20 bg-sky-400/10 text-sky-100",
};

const insightToneClasses: Record<InsightTone, string> = {
  positive: "border-emerald-400/20 bg-emerald-400/10 text-emerald-100",
  caution: "border-amber-400/20 bg-amber-400/10 text-amber-100",
  opportunity: "border-fuchsia-400/20 bg-fuchsia-400/10 text-fuchsia-100",
};

const insightToneIcons: Record<InsightTone, LucideIcon> = {
  positive: BadgeCheck,
  caution: CircleAlert,
  opportunity: Sparkles,
};

function buildChartGeometry(points: ReportPoint[]) {
  const values = points.map((point) => point.value);
  const max = Math.max(...values);
  const min = Math.min(...values);
  const range = Math.max(max - min, 1);

  const coordinates = points.map((point, index) => {
    const x = points.length === 1 ? 50 : (index / (points.length - 1)) * 100;
    const y = 84 - ((point.value - min) / range) * 52;
    return { ...point, x, y };
  });

  const linePath = coordinates
    .map((point, index) => `${index === 0 ? "M" : "L"} ${point.x} ${point.y}`)
    .join(" ");

  const areaPath = `${linePath} L 100 92 L 0 92 Z`;

  return { coordinates, linePath, areaPath };
}

function MetricTooltip({ text, compact }: { text: string; compact: boolean }) {
  const tooltipId = useId();

  return (
    <span className="group/tooltip relative inline-flex items-center">
      <button
        type="button"
        aria-describedby={tooltipId}
        className="inline-flex h-5 w-5 items-center justify-center rounded-full border border-white/10 bg-white/5 text-slate-400 transition-colors hover:border-white/20 hover:text-white focus:outline-none focus:ring-2 focus:ring-fuchsia-300/40"
      >
        <Info className="h-3.5 w-3.5" />
      </button>
      <span
        id={tooltipId}
        role="tooltip"
        className={`pointer-events-none absolute bottom-[calc(100%+0.65rem)] left-1/2 z-30 w-52 -translate-x-1/2 rounded-2xl border border-slate-700 bg-[rgba(2,6,23,0.98)] px-3 py-2 text-left text-[11px] leading-relaxed text-slate-200 opacity-0 shadow-[0_18px_50px_rgba(2,6,23,0.72)] ring-1 ring-white/5 backdrop-blur-xl transition-all duration-200 group-hover/tooltip:translate-y-0 group-hover/tooltip:opacity-100 group-focus-within/tooltip:translate-y-0 group-focus-within/tooltip:opacity-100 ${
          compact ? "translate-y-1" : "translate-y-2"
        }`}
      >
        {text}
        <span className="absolute left-1/2 top-full h-2.5 w-2.5 -translate-x-1/2 -translate-y-1/2 rotate-45 border-b border-r border-slate-700 bg-[rgba(2,6,23,0.98)]" />
      </span>
    </span>
  );
}

export function SampleReportPreview({
  compact = false,
  initialRange = "daily",
  ranges = defaultRangeOrder,
}: {
  compact?: boolean;
  initialRange?: ReportRange;
  ranges?: ReportRange[];
}) {
  const safeInitialRange = ranges.includes(initialRange) ? initialRange : ranges[0] ?? "daily";
  const [activeRange, setActiveRange] = useState<ReportRange>(safeInitialRange);
  const [expandedInsightId, setExpandedInsightId] = useState(reportDatasets[safeInitialRange].insights[0]?.id ?? "");
  const dataset = reportDatasets[activeRange];
  const { coordinates, linePath, areaPath } = buildChartGeometry(dataset.chart);

  const setRange = (nextRange: ReportRange) => {
    setActiveRange(nextRange);
    setExpandedInsightId(reportDatasets[nextRange].insights[0]?.id ?? "");
  };

  return (
    <div
      className={`relative overflow-hidden rounded-[2rem] border border-slate-800/80 bg-[radial-gradient(circle_at_top_left,rgba(192,132,252,0.18),transparent_34%),radial-gradient(circle_at_bottom_right,rgba(34,211,238,0.12),transparent_28%),linear-gradient(180deg,rgba(2,6,23,0.98),rgba(15,23,42,0.97))] ${
        compact ? "h-full p-4 md:p-5" : "p-4 md:p-5"
      } shadow-[0_30px_90px_rgba(2,6,23,0.55)]`}
    >
      <div className="pointer-events-none absolute inset-0">
        <div className="absolute left-[10%] top-[-8%] h-28 w-28 rounded-full bg-fuchsia-400/12 blur-3xl" />
        <div className="absolute bottom-[-10%] right-[8%] h-32 w-32 rounded-full bg-cyan-300/10 blur-3xl" />
        <div className="absolute inset-0 bg-[linear-gradient(120deg,transparent,rgba(255,255,255,0.05),transparent)] opacity-40" />
      </div>

      <div className="pointer-events-none absolute inset-0 z-10 flex items-center justify-center overflow-hidden px-4">
        <span className={`select-none font-semibold uppercase text-white/[0.05] [transform:rotate(-18deg)] ${compact ? "text-[clamp(2.2rem,7vw,4.8rem)] tracking-[0.28em]" : "text-[clamp(2.8rem,9vw,6.5rem)] tracking-[0.5em]"}`}>
          Sample Report
        </span>
      </div>

      <div className="relative z-20 flex h-full flex-col gap-3.5">
        <div className="flex flex-col gap-3 lg:flex-row lg:items-start lg:justify-between">
          <div className="max-w-2xl">
            <div className="mb-2 flex flex-wrap items-center gap-2">
              <span className="rounded-full border border-amber-300/20 bg-amber-300/10 px-3 py-1 text-[11px] font-semibold uppercase tracking-[0.22em] text-amber-100 shadow-[0_10px_25px_rgba(251,191,36,0.12)]">
                Sample report
              </span>
              <span className="rounded-full border border-white/10 bg-white/5 px-3 py-1 text-[11px] font-medium uppercase tracking-[0.18em] text-slate-300">
                {dataset.sentAt}
              </span>
            </div>
            <h3 className={`${compact ? "text-xl md:text-2xl" : "text-[1.75rem] md:text-[2rem]"} font-semibold leading-tight text-white`}>
              {dataset.headline}
            </h3>
            <p className={`mt-2 max-w-2xl text-slate-300 ${compact ? "text-sm md:text-[15px]" : "text-sm md:text-[15px]"}`}>
              {dataset.message}
            </p>
          </div>

          <div className={`inline-flex w-full items-center rounded-full border border-white/10 bg-slate-950/75 p-1.5 shadow-[0_12px_30px_rgba(2,6,23,0.45)] ${compact ? "max-w-full flex-wrap justify-start gap-1.5" : "max-w-max"}`}>
            {ranges.map((range) => {
              const isActive = range === activeRange;

              return (
                <button
                  key={range}
                  type="button"
                  onClick={() => setRange(range)}
                  className={`rounded-full px-4 py-2 text-sm font-semibold transition-all duration-300 ${
                    isActive
                      ? "bg-gradient-to-r from-fuchsia-400/90 via-purple-300/90 to-cyan-300/90 text-slate-950 shadow-[0_12px_28px_rgba(168,85,247,0.35)]"
                      : "text-slate-300 hover:text-white"
                  }`}
                >
                  <span className={compact ? "text-xs md:text-sm" : undefined}>
                    {compact ? compactRangeLabels[range] : reportDatasets[range].label}
                  </span>
                </button>
              );
            })}
          </div>
        </div>

        <div className="flex flex-wrap items-center gap-2 rounded-[1.45rem] border border-white/10 bg-slate-950/62 px-4 py-3 backdrop-blur-sm">
          <span className="inline-flex items-center gap-2 rounded-full border border-amber-300/20 bg-amber-300/12 px-3 py-1.5 text-xs font-medium text-amber-100 shadow-[0_10px_20px_rgba(251,191,36,0.12)]">
            <TrendingUp className="h-3.5 w-3.5" />
            Clear action waiting
          </span>
          <p className={`text-slate-200 ${compact ? "text-sm" : "text-sm md:text-[15px]"}`}>{dataset.summaryLine}</p>
        </div>

        <div className={`grid flex-1 gap-3.5 ${compact ? "2xl:grid-cols-[1.06fr_0.94fr]" : "xl:grid-cols-[1.12fr_0.88fr]"}`}>
          <div className="grid gap-4">
            <div className={`grid gap-3 ${compact ? "sm:grid-cols-2 xl:grid-cols-3" : "sm:grid-cols-3"}`}>
              {dataset.metrics.map((metric) => (
                <div
                  key={metric.label}
                  className="rounded-[1.35rem] border border-white/10 bg-slate-950/70 px-4 py-3.5 shadow-[0_16px_40px_rgba(2,6,23,0.35)] transition-transform duration-300 hover:-translate-y-0.5"
                >
                  <div className="flex items-start justify-between gap-3">
                    <div>
                      <p className="text-[11px] font-semibold uppercase tracking-[0.2em] text-slate-500">{metric.label}</p>
                    </div>
                    <MetricTooltip text={metric.tooltip} compact={compact} />
                  </div>
                  <p className={`${compact ? "text-[1.7rem]" : "text-[1.7rem]"} mt-2.5 font-semibold tracking-tight text-white`}>
                    {metric.value}
                  </p>
                  <span className={`mt-2.5 inline-flex rounded-full border px-2.5 py-1 text-xs font-medium ${metricToneClasses[metric.tone]}`}>
                    {metric.delta}
                  </span>
                </div>
              ))}
            </div>

            <div className="rounded-[1.55rem] border border-white/10 bg-slate-950/72 p-4 shadow-[0_20px_50px_rgba(2,6,23,0.4)]">
              <div className="mb-3 flex flex-col gap-2 md:flex-row md:items-start md:justify-between">
                <div>
                  <p className="text-[11px] font-semibold uppercase tracking-[0.22em] text-slate-500">{dataset.chartTitle}</p>
                </div>
                <span className="inline-flex items-center gap-2 rounded-full border border-amber-300/20 bg-amber-300/12 px-3 py-1.5 text-xs font-medium text-amber-100">
                  <Sparkles className="h-3.5 w-3.5" />
                  Signal is improving
                </span>
              </div>

              <div className="rounded-[1.35rem] border border-white/10 bg-[linear-gradient(180deg,rgba(15,23,42,0.82),rgba(2,6,23,0.92))] p-3 md:p-4">
                <div className="aspect-[2.2/1] w-full">
                  <svg viewBox="0 0 100 100" className="h-full w-full overflow-visible" preserveAspectRatio="xMidYMid meet" aria-hidden="true">
                    {[20, 40, 60, 80].map((value) => (
                      <line
                        key={value}
                        x1="0"
                        x2="100"
                        y1={value}
                        y2={value}
                        stroke="rgba(255,255,255,0.10)"
                        strokeDasharray="2 4"
                      />
                    ))}
                    <path d={areaPath} fill="rgba(244,114,182,0.10)" />
                    <path d={linePath} fill="none" stroke="rgb(103,232,249)" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" />
                    {coordinates.map((point, index) => {
                      const isLastPoint = index === coordinates.length - 1;

                      return (
                        <g key={point.label}>
                          {isLastPoint ? <circle cx={point.x} cy={point.y} r="6.2" fill="rgba(251,191,36,0.18)" /> : null}
                          <circle cx={point.x} cy={point.y} r={isLastPoint ? "3.2" : "2.2"} fill={isLastPoint ? "rgb(251,191,36)" : "rgb(186,230,253)"} />
                        </g>
                      );
                    })}
                  </svg>
                </div>

                <div className="mt-3 grid grid-cols-2 gap-2 sm:grid-cols-4">
                  {dataset.chart.slice(-4).map((point) => (
                    <div key={point.label} className="rounded-2xl border border-white/10 bg-white/[0.03] px-3 py-2 text-center">
                      <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-slate-500">{point.label}</p>
                      <p className="mt-1 text-sm font-semibold text-white">{point.display}</p>
                      <div className="mt-2 h-1.5 rounded-full bg-white/8">
                        <div
                          className="h-1.5 rounded-full bg-gradient-to-r from-amber-300 via-orange-300 to-cyan-300"
                          style={{ width: `${Math.max(18, (point.value / dataset.chart[dataset.chart.length - 1].value) * 100)}%` }}
                        />
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>

          <div className="grid gap-4">
            <div className="rounded-[1.55rem] border border-amber-300/20 bg-[linear-gradient(180deg,rgba(251,191,36,0.18),rgba(249,115,22,0.12),rgba(15,23,42,0.9))] p-4 shadow-[0_22px_60px_rgba(180,83,9,0.24)]">
              <div className="flex items-start justify-between gap-3">
                <div>
                  <p className="text-[11px] font-semibold uppercase tracking-[0.22em] text-amber-100/90">Next best action</p>
                  <h4 className={`mt-2 font-semibold text-white ${compact ? "text-lg" : "text-xl"}`}>{dataset.actionTitle}</h4>
                </div>
                <span className="inline-flex h-10 w-10 items-center justify-center rounded-2xl border border-amber-200/20 bg-amber-300/14 text-amber-100 shadow-[0_10px_24px_rgba(251,191,36,0.16)]">
                  <ArrowUpRight className="h-4.5 w-4.5" />
                </span>
              </div>
              <p className={`mt-3 text-slate-200 ${compact ? "text-sm" : "text-[15px]"}`}>{dataset.actionBody}</p>
              <div className="mt-3 grid gap-2 sm:grid-cols-2">
                <div className="rounded-2xl border border-white/10 bg-black/20 px-3 py-3">
                  <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-slate-400">Window</p>
                  <p className="mt-1 text-sm font-medium text-white">{dataset.actionWindow}</p>
                </div>
                <div className="rounded-2xl border border-white/10 bg-black/20 px-3 py-3">
                  <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-slate-400">Expected outcome</p>
                  <p className="mt-1 text-sm font-medium text-white">{dataset.actionImpact}</p>
                </div>
              </div>
            </div>

            <div className={`grid gap-4 ${compact ? "xl:grid-cols-2 2xl:grid-cols-1" : ""}`}>
              {dataset.insights.map((insight) => {
                const isExpanded = insight.id === expandedInsightId;
                const Icon = insightToneIcons[insight.tone];

                return (
                  <button
                    key={insight.id}
                    type="button"
                    onClick={() => setExpandedInsightId(insight.id)}
                    className={`rounded-[1.4rem] border px-4 py-4 text-left transition-all duration-300 ${
                      isExpanded
                        ? "scale-[1.015] border-white/15 bg-slate-900/88 shadow-[0_20px_50px_rgba(2,6,23,0.4)]"
                        : "border-white/10 bg-slate-950/70 hover:border-white/15 hover:bg-slate-900/82"
                    }`}
                    aria-pressed={isExpanded}
                  >
                    <div className="flex items-start justify-between gap-3">
                      <div className="flex items-start gap-3">
                        <span className={`inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-2xl border ${insightToneClasses[insight.tone]}`}>
                          <Icon className="h-4 w-4" />
                        </span>
                        <div>
                          <p className="text-base font-semibold text-white">{insight.title}</p>
                          <p className="mt-1 text-sm leading-relaxed text-slate-300">{insight.summary}</p>
                        </div>
                      </div>
                      <span className="rounded-full border border-white/10 bg-white/5 px-2.5 py-1 text-[10px] font-semibold uppercase tracking-[0.16em] text-slate-400">
                        Expand
                      </span>
                    </div>

                    <div
                      className={`grid transition-all duration-300 ${isExpanded ? "mt-4 grid-rows-[1fr] opacity-100" : "grid-rows-[0fr] opacity-60"}`}
                    >
                      <div className="overflow-hidden">
                        <p className="text-sm leading-relaxed text-slate-300">{insight.detail}</p>
                        <div className="mt-3 flex flex-wrap gap-2">
                          <span className="rounded-full border border-white/10 bg-white/5 px-3 py-1.5 text-xs font-medium text-slate-200">
                            Owner: {insight.owner}
                          </span>
                          <span className="rounded-full border border-white/10 bg-white/5 px-3 py-1.5 text-xs font-medium text-slate-200">
                            Why now: {insight.impact}
                          </span>
                        </div>
                      </div>
                    </div>
                  </button>
                );
              })}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}